import React, { useMemo } from 'react';
import { graphql, useStaticQuery, Link as GatsbyLink } from 'gatsby';
import { SegmentVariants, SegmentVariant, VariantFiltersById } from '@printdeal/segmentation/react';
import { Card, Grid, Heading } from '@printdeal/ui-library';
import ContentfulCardImage from '../../../ContentfulCardImage';

interface ProductData {
  path: string;
  title: string;
  subtitle: string;
  featuredImage: {
    url: string;
    title: string;
  };
}

interface ProductListData {
  contentful_id: string;
  title: string;
  list: ProductData[];
  audienceType: string;
}

const query = graphql`
  query {
    accountDashboardPage: allContentfulPageSystem(filter: { pageSytemType: { eq: "accountDashboard" } }) {
      edges {
        node {
          productLists {
            contentful_id
            audienceType
            title
            list {
              path
              title
              subtitle
              featuredImage {
                ...ContentfulCardImage
              }
            }
          }
        }
      }
    }
  }
`;

const ProductForAudiences = () => {
  const { accountDashboardPage: { edges: [{ node: { productLists } }] } } = useStaticQuery(query);

  const variantFilters = useMemo(() => {
    if (productLists.length === 0) {
      return [];
    }

    return productLists.reduce((acc: VariantFiltersById[], productList: ProductListData) => {
      const audiences = productList.audienceType.split(',');

      if (audiences.length > 0) {
        acc.push({
          id: productList.contentful_id,
          filters: {
            audienceWhitelist: {
              audiences,
              matchType: 'any',
            },
          },
        });
      }
      return acc;
    }, []);
  }, [productLists]);

  if (productLists.length === 0) {
    return null;
  }

  return (
    <SegmentVariants
      variantFilters={variantFilters}
    >
      {
        productLists.map((productList: ProductListData) => (
          <SegmentVariant id={productList.contentful_id}>
            <Heading size="2" variant="display" className="pd-mb-4 pd-mt-6">{productList.title}</Heading>
            <Grid className="pd-grid-cols-1 pd-gap-5 sm:pd-grid-cols-2">
              {
                productList.list.map((product: ProductData) => (
                  <Grid.Item
                    key={product.path}
                    as={Card}
                    className="pd-items-start"
                    data-cy="productCard"
                    link={product.path ? (
                      <GatsbyLink
                        to={product.path}
                      />
                    ) : (<> </>)}
                    image={product.featuredImage && (
                      <ContentfulCardImage
                        src={product.featuredImage.url}
                        alt={product.featuredImage.title}
                      />
                    )}
                    title={product.title || ''}
                    variant="horizontal"
                  >
                    {product?.subtitle}
                  </Grid.Item>
                ))
              }
            </Grid>
          </SegmentVariant>
        ))
      }
    </SegmentVariants>
  );
};

export default ProductForAudiences;
